import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "@/utils/api";
import { FileText, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function DebitNote() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    apiGet("/adjustments?direction=subsequent_debit")
      .then((r) => setNotes(r.items || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = notes.filter(
    (n) =>
      n.adjustment_id?.toLowerCase().includes(search.toLowerCase()) ||
      n.vendor_id?.toLowerCase().includes(search.toLowerCase()) ||
      n.source_sap_invoice_id?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-heading font-semibold text-slate-900">Supplier Debit Notes</h2>
          <p className="text-[11px] text-slate-400 font-mono mt-0.5">Raise debit notes against supplier invoices</p>
        </div>
        <Button
          onClick={() => navigate("/invoices/adjustment?direction=subsequent_debit")}
          className="gap-2 text-sm h-9"
          style={{ backgroundColor: "#0A6755", color: "#fff" }}
          data-testid="create-debit-note-btn"
        >
          <Plus size={14} /> New Debit Note
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search by vendor or note #"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8 h-9 text-sm"
          data-testid="debit-note-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="hidden grid-cols-5 px-4 py-2.5 bg-slate-50 border-b border-slate-200 sm:grid">
          {["Debit Note #", "Vendor", "Reference Invoice", "Amount", "Status"].map((h) => (
            <span key={h} className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{h}</span>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-5 h-5 border-2 border-[#0A6755] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <FileText size={18} className="text-slate-400" />
            </div>
            <p className="text-[13px] text-slate-500" data-testid="empty-debit-notes-message">No debit notes found</p>
            <p className="text-[11px] text-slate-400">Create your first supplier debit note</p>
          </div>
        ) : (
          filtered.map((n) => (
            <div key={n.adjustment_id} className="grid grid-cols-1 gap-1 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 sm:grid-cols-5 sm:gap-0" data-testid={`debit-note-row-${n.adjustment_id}`}>
              <span className="text-[13px] font-mono text-slate-900" data-testid={`debit-note-id-${n.adjustment_id}`}>{n.adjustment_id}</span>
              <span className="text-[13px] text-slate-700" data-testid={`debit-note-vendor-${n.adjustment_id}`}>{n.vendor_id}</span>
              <span className="text-[13px] font-mono text-slate-500" data-testid={`debit-note-source-${n.adjustment_id}`}>{n.source_sap_invoice_id}</span>
              <span className="text-[13px] text-slate-900" data-testid={`debit-note-amount-${n.adjustment_id}`}>₹{Number(n.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 w-fit" data-testid={`debit-note-status-${n.adjustment_id}`}>{n.status}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
